// Global popup-open tracker for the Arsenal "Popups → Live" feed. Watches
// document.body for popup/modal/overlay/sheet nodes appearing and logs each open
// to sessionStorage (a ring buffer) + fires a window event. Non-invasive: it
// needs no per-popup instrumentation, so it catches the ~55 decentralised popups
// that have no central manager. Install once, globally (see index.js).

const LOG_KEY = "pw_popup_track_log_v1";
const MAX_ENTRIES = 60;
export const POPUP_EVENT = "pw:popup-open";

const POPUP_SELECTOR =
  '[role="dialog"],[class*="modal"],[class*="Modal"],[class*="popup"],[class*="Popup"],[class*="sheet"],[class*="Sheet"],[class*="overlay"],[class*="Overlay"],[class*="drawer"],[class*="lightbox"]';

function classOf(el) {
  return el && typeof el.className === "string" ? el.className : "";
}

function looksLikePopup(el) {
  if (!(el instanceof HTMLElement)) return false;
  if (el.getAttribute && el.getAttribute("role") === "dialog") return true;
  if (/\b(modal|popup|overlay|sheet|drawer|dialog|lightbox|backdrop)\b/i.test(classOf(el))) return true;
  try {
    const cs = window.getComputedStyle(el);
    if ((cs.position === "fixed" || cs.position === "absolute") && Number(cs.zIndex) >= 1000) {
      const rect = el.getBoundingClientRect();
      if (rect.width > 180 && rect.height > 120) return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

function nameFor(el) {
  const label =
    (el.getAttribute && (el.getAttribute("data-popup") || el.getAttribute("data-testid") || el.getAttribute("aria-label"))) || "";
  if (label) return String(label).slice(0, 56);
  const cls = classOf(el)
    .split(/\s+/)
    .find((c) => /modal|popup|sheet|drawer|dialog|overlay/i.test(c));
  if (cls) return cls.slice(0, 56);
  const heading = el.querySelector && el.querySelector("h1,h2,h3,strong,b");
  const text = heading && heading.textContent ? heading.textContent.trim() : "";
  if (text) return text.slice(0, 56);
  return (el.tagName || "popup").toLowerCase();
}

function readLog() {
  try {
    const raw = window.sessionStorage.getItem(LOG_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLog(list) {
  try {
    window.sessionStorage.setItem(LOG_KEY, JSON.stringify(list.slice(-MAX_ENTRIES)));
  } catch {
    /* storage full/unavailable */
  }
}

export function getPopupLog() {
  return readLog();
}

export function clearPopupLog() {
  writeLog([]);
  try {
    window.dispatchEvent(new CustomEvent(POPUP_EVENT));
  } catch {
    /* ignore */
  }
}

let installed = false;

export function installPopupTracker() {
  if (installed || typeof window === "undefined" || typeof MutationObserver === "undefined" || !document.body) return;
  installed = true;
  const seen = new WeakSet();

  const record = (el) => {
    if (seen.has(el)) return;
    seen.add(el);
    const entry = { name: nameFor(el), path: window.location ? window.location.pathname : "", ts: Date.now() };
    const list = readLog();
    list.push(entry);
    writeLog(list);
    try {
      window.dispatchEvent(new CustomEvent(POPUP_EVENT, { detail: entry }));
    } catch {
      /* ignore */
    }
  };

  const scan = (node) => {
    if (!(node instanceof HTMLElement)) return;
    if (looksLikePopup(node)) record(node);
    if (node.querySelectorAll) {
      node.querySelectorAll(POPUP_SELECTOR).forEach((child) => {
        if (looksLikePopup(child)) record(child);
      });
    }
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) scan(node);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}
